import { api } from '../../api/client.js';
import type { AlertEmailsPanelData } from '../../api/types.js';
import { useAsync } from '../../hooks/useAsync.js';
import { formatRunDate } from '../Audit/shared.js';
import { Skeleton } from '../States/States.js';
import { color, fontSize, radius } from '../../theme/tokens.js';

/**
 * Settings → Alert emails (spec 008, PRD-Alerts §8.9, FR-AL56/AL57) — the third admin capability
 * constitution 4.0.0 allows: READ-ONLY, and counts plus the names of people not emailed. No email
 * body, no recipient list: who was emailed is already on the Alerts page, for everyone.
 *
 * ⚠ The panel says when the job has STOPPED: a run due three hours ago with nothing processed turns
 * the job line amber. Without it, a silent job looks exactly like a quiet week.
 * ⚠ No "on alert" or "newly flagged" column: those are audit figures, which the notifier's record may
 * not hold (constitution condition 4, FR-AL55). The page's banner carries how many are on alert.
 */

const MODE_FACE: Record<AlertEmailsPanelData['mode'], string> = {
  off: 'Off',
  'dry-run': 'Dry run — nothing is sent',
  redirect: 'Test — sent to testers',
  live: 'Live',
};

const REASON: Record<AlertEmailsPanelData['notEmailed'][number]['reason'], string> = {
  inactive: 'no longer active in the HR directory',
  not_found: 'not found in the HR directory',
  excluded: 'on the never-email list',
  domain: 'outside the company domains',
  mismatch: 'their HR manager differs from the alignment sheet (emails follow the sheet)',
};

/** `settled`: the run is done and its daily alerts wait for 9:00 AM Eastern (2026-10-30). */
const STATUS_FACE: Partial<Record<AlertEmailsPanelData['runs'][number]['status'], string>> = {
  settled: 'waiting for 9 AM',
  refused_volume: 'refused (too many)',
};

function easternTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function Line({ label, children, tone }: { label: string; children: React.ReactNode; tone?: 'warn' }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '4px 0', fontSize: fontSize.control }}>
      <span style={{ width: 150, flex: 'none', color: color.inkMuted }}>{label}</span>
      <span style={{ color: tone === 'warn' ? color.warning : color.inkBody, fontWeight: tone === 'warn' ? 600 : 400 }}>{children}</span>
    </div>
  );
}

export function AlertEmailsPanel() {
  const state = useAsync(() => api.alertEmails(), []);
  const data = state.data;

  return (
    <section
      aria-label="Alert emails"
      style={{ background: color.surface, border: `1px solid ${color.border}`, borderRadius: radius['3xl'], padding: '16px 20px', marginBottom: 20 }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
        <div style={{ fontSize: fontSize.control, fontWeight: 600, color: color.ink, marginRight: 'auto' }}>Alert emails</div>
        {data ? <div style={{ fontSize: fontSize.caption, color: color.inkMuted }}>Mode: {MODE_FACE[data.mode]}</div> : null}
      </div>

      {!data ? (
        state.error ? (
          <div role="alert" style={{ fontSize: fontSize.caption, color: color.dangerInk }}>Could not load the alert email activity.</div>
        ) : (
          <Skeleton height={120} />
        )
      ) : data.mode === 'off' && !data.job.lastRanAt ? (
        <div style={{ fontSize: fontSize.control, color: color.inkBody }}>
          Alert emails are switched off. Switch on in dry-run to see how many emails would be sent.
        </div>
      ) : (
        <>
          <Line label="Email job" tone={data.job.behind ? 'warn' : undefined}>
            {data.job.behind
              ? `The email job has not processed the ${formatRunDate(data.job.behind)} run.`
              : data.job.lastRanAt
                ? `last ran ${easternTime(data.job.lastRanAt)} Eastern${data.job.lastProcessedRun ? ` · processed the ${formatRunDate(data.job.lastProcessedRun)} run` : ''}`
                : 'has not run yet'}
          </Line>
          {data.next ? (
            <>
              <Line label={`Next run (${formatRunDate(data.next.runDate)})`}>
                about {data.next.daily} daily alert{data.next.daily === 1 ? '' : 's'} — an estimate: one per recruiter flagged by a daily audit on the latest run
                {data.next.thriceWeekly ? ` · + ${data.next.thriceWeekly} Tue/Thu/Mon email${data.next.thriceWeekly === 1 ? '' : 's'} on those days` : ''}
              </Line>
              <Line label={`Next Monday (${formatRunDate(data.next.nextMonday)})`}>
                + {data.next.recruiterWeekly} recruiter weekly report{data.next.recruiterWeekly === 1 ? '' : 's'}
                {' · '}+ {data.next.weekly} weekly team report{data.next.weekly === 1 ? '' : 's'}
                {data.next.ccLoad.length ? ` · ${data.next.ccLoad.map((c) => `${c.name} copied on ${c.reports}`).join(', ')}` : ''}
              </Line>
            </>
          ) : null}
          {data.ifLive ? (
            <Line label="If switched to live">
              {[
                `~${data.ifLive.perWeekday} a weekday`,
                // Only when the Tue/Thu/Mon email carries anything — otherwise it is the weekday figure again.
                data.ifLive.tuesdayThursday !== data.ifLive.perWeekday ? `~${data.ifLive.tuesdayThursday} on Tuesdays and Thursdays` : null,
                `~${data.ifLive.monday} on Mondays`,
                `~${data.ifLive.perWeek} a week`,
              ]
                .filter(Boolean)
                .join(', ')}
            </Line>
          ) : null}
          <Line label="Last 7 days">
            {data.last7Days} email{data.last7Days === 1 ? '' : 's'}
          </Line>

          {data.notEmailed.length ? (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: fontSize.caption, fontWeight: 600, color: color.inkMuted, textTransform: 'uppercase', letterSpacing: '.5px' }}>
                Not emailed
              </div>
              <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: fontSize.control, color: color.inkBody }}>
                {data.notEmailed.map((p, i) => (
                  <li key={i}>
                    {p.level} · {p.name ?? 'unnamed'} — {REASON[p.reason]}
                    {p.level !== 'L1' && p.recruiters ? ` (${p.recruiters} recruiters' report)` : ''}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {data.runs.length ? (
            <table aria-label="Last 14 runs" style={{ marginTop: 12, borderCollapse: 'collapse', fontSize: fontSize.caption, width: '100%' }}>
              <thead>
                <tr style={{ color: color.inkMuted, textAlign: 'right' }}>
                  {['Run date', 'Status', 'Daily', 'Tue/Thu/Mon', 'Rec. weekly', 'Team weekly', 'Total', 'Dropped', 'Failed', 'Unknown'].map((h, i) => (
                    <th key={h} style={{ padding: '4px 8px', fontWeight: 600, textAlign: i < 2 ? 'left' : 'right' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.runs.map((r) => (
                  <tr key={`${r.runDate}|${r.mode}`} style={{ borderTop: `1px solid ${color.borderSubtle}`, color: color.inkBody }}>
                    <td style={{ padding: '4px 8px' }}>{formatRunDate(r.runDate)}</td>
                    <td style={{ padding: '4px 8px' }}>{STATUS_FACE[r.status] ?? r.status}</td>
                    {[r.daily, r.thriceWeekly, r.recruiterWeekly, r.weekly, r.daily + r.thriceWeekly + r.recruiterWeekly + r.weekly, r.dropped, r.failed, r.unknown].map((n, i) => (
                      <td key={i} className="mono" style={{ padding: '4px 8px', textAlign: 'right' }}>{n}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </>
      )}
    </section>
  );
}
